/**
 * Right-side drawer showing the reviewer's pending comments before they submit.
 * Each row is editable inline and seekable (click timecode → seek the player).
 *
 * Styling: dark, glassy, matches the public-viewer aesthetic.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Pencil, Trash2, X, Check, Send } from 'lucide-react';
import { Button } from '../../ui/button';
import { AutoResizeTextarea } from '../../ui/auto-resize-textarea';
import { useReviewSession } from './useReviewSession';
import { formatTimecode } from './format';

interface Props {
  open: boolean;
  onClose: () => void;
  onSeek: (seconds: number, slideId: string) => void;
  onSubmitted: (count: number) => void;
}

export function MyReviewDrawer({ open, onClose, onSeek, onSubmitted }: Props) {
  const drafts = useReviewSession((s) => s.drafts);
  const submitting = useReviewSession((s) => s.submitting);
  const lastError = useReviewSession((s) => s.lastError);
  const updateDraft = useReviewSession((s) => s.updateDraft);
  const removeDraft = useReviewSession((s) => s.removeDraft);
  const submit = useReviewSession((s) => s.submit);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const startEdit = (id: string, currentText: string) => {
    setEditingId(id);
    setEditText(currentText);
  };
  const saveEdit = () => {
    if (editingId && editText.trim()) {
      updateDraft(editingId, editText.trim());
    }
    setEditingId(null);
    setEditText('');
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleSubmit = async () => {
    try {
      const res = await submit();
      onSubmitted(res.commentCount);
    } catch {
      // error already in store
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-30 bg-black/40"
            onClick={onClose}
          />
          <motion.aside
            key="drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="fixed top-0 right-0 z-40 h-full w-[380px] max-w-[90vw] bg-zinc-900/98 backdrop-blur-xl border-l border-white/10 text-white flex flex-col shadow-2xl"
            role="dialog"
            aria-label="My review"
          >
            <header className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
              <div>
                <h2 className="text-sm font-semibold">Your review</h2>
                <p className="text-xs text-white/50">{drafts.length} comment{drafts.length === 1 ? '' : 's'} pending</p>
              </div>
              <button
                onClick={onClose}
                className="text-white/50 hover:text-white p-1.5 rounded hover:bg-white/10"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto">
              {drafts.length === 0 ? (
                <div className="text-center py-16 px-6">
                  <div className="text-3xl mb-3">💬</div>
                  <p className="text-sm text-white/70">No comments yet.</p>
                  <p className="text-xs text-white/40 mt-1">
                    Pause the video and press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px] font-mono">c</kbd> to add one.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-white/5">
                  {drafts.map((d) => (
                    <li key={d.localId} className="px-4 py-3 group hover:bg-white/[0.02]">
                      <button
                        onClick={() => onSeek(d.timestamp, d.slideId)}
                        className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white"
                      >
                        <span className="px-1.5 py-0.5 bg-white/10 rounded font-mono tabular-nums">
                          {formatTimecode(d.timestamp)}
                        </span>
                        <span className="truncate">
                          Slide {d.slideIndex + 1}
                          {d.slideTitle ? ` · ${d.slideTitle}` : ''}
                        </span>
                      </button>
                      {editingId === d.localId ? (
                        <div className="mt-2">
                          <AutoResizeTextarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            autoFocus
                            maxLength={2000}
                            minRows={2}
                            maxRows={8}
                            className="w-full bg-transparent border border-white/20 focus:border-white/40 rounded-md px-2.5 py-2 text-sm text-white placeholder:text-white/40 resize-none focus:outline-none"
                          />
                          <div className="flex items-center justify-end gap-1 mt-1.5">
                            <button
                              onClick={cancelEdit}
                              className="text-xs text-white/60 hover:text-white px-2 py-1 rounded hover:bg-white/10"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={saveEdit}
                              disabled={!editText.trim()}
                              className="text-xs text-white px-2 py-1 rounded bg-white/15 hover:bg-white/25 disabled:opacity-40 inline-flex items-center gap-1"
                            >
                              <Check className="h-3 w-3" /> Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-1.5 flex items-start justify-between gap-2">
                          <p className="text-sm text-white/90 whitespace-pre-wrap break-words flex-1">{d.text}</p>
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                            <button
                              onClick={() => startEdit(d.localId, d.text)}
                              className="text-white/50 hover:text-white p-1 rounded hover:bg-white/10"
                              aria-label="Edit"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => removeDraft(d.localId)}
                              className="text-white/50 hover:text-red-400 p-1 rounded hover:bg-white/10"
                              aria-label="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <footer className="border-t border-white/10 p-3 flex-shrink-0">
              {lastError && (
                <p className="text-xs text-red-400 mb-2">{lastError}</p>
              )}
              <Button
                onClick={handleSubmit}
                disabled={drafts.length === 0 || submitting}
                className="w-full"
              >
                <Send className="h-4 w-4 mr-2" />
                {submitting ? 'Sending…' : `Send ${drafts.length} comment${drafts.length === 1 ? '' : 's'} to creator`}
              </Button>
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
